import React, { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, getDoc, doc, updateDoc, addDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from './firebase';

/* ══════════════════════════════════════════════
   TECH PORTAL - Mobile-first technician interface
   ══════════════════════════════════════════════ */

export default function Tech() {
  const [user, setUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');

  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [lastPunch, setLastPunch] = useState(null);
  const [timePunches, setTimePunches] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [toast, setToast] = useState(null); // { message, type: 'error' | 'success' | 'info' }

  // Toast helper — auto-dismisses
  const showToast = (message, type = 'info', duration = 4000) => {
    setToast({ message, type });
    if (duration > 0) {
      setTimeout(() => setToast(null), duration);
    }
  };

  /* ── Auth ── */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        const empSnap = await getDoc(doc(db, 'employees', u.uid));
        if (empSnap.exists()) {
          setUser(u);
          setEmployee({ id: empSnap.id, ...empSnap.data() });
        } else {
          setUser(u);
          setEmployee(null);
        }
      } else {
        setUser(null);
        setEmployee(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  /* ── Load jobs for this tech ── */
  const loadData = useCallback(async () => {
    if (!employee?.name) return;
    try {
      const woSnap = await getDocs(query(collection(db, 'workOrders'), orderBy('createdAt', 'desc')));
      const allJobs = woSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const myJobs = allJobs.filter(w => w.assignedTo === employee.name);
      setJobs(myJobs);

      // Get time punches
      const tpSnap = await getDocs(query(collection(db, 'timePunches'), where('employeeId', '==', employee.id), orderBy('timestamp', 'desc')));
      const punches = tpSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTimePunches(punches);
      if (punches.length > 0) setLastPunch(punches[0]);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  }, [employee]);

  useEffect(() => {
    if (employee) loadData();
  }, [employee, loadData]);

  /* ── Tick every second while clocked in (for elapsed timer) ── */
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, loginEmail, loginPass);
    } catch {
      setLoginError('Invalid credentials.');
    }
  };

  /* ── Clock In/Out ── */
  const isClockedIn = lastPunch && lastPunch.type === 'in';

  const handleClockInOut = async () => {
    if (!employee) return;
    try {
      if (isClockedIn) {
        // Clock out
        const inTime = lastPunch.timestamp?.seconds ? new Date(lastPunch.timestamp.seconds * 1000) : new Date();
        const hoursWorked = (Date.now() - inTime.getTime()) / (1000 * 60 * 60);
        await addDoc(collection(db, 'timePunches'), {
          employeeId: employee.id,
          employeeName: employee.name,
          type: 'out',
          timestamp: serverTimestamp(),
          punchPairId: lastPunch.id,
          hoursWorked: Number(hoursWorked.toFixed(2)),
        });
      } else {
        await addDoc(collection(db, 'timePunches'), {
          employeeId: employee.id,
          employeeName: employee.name,
          type: 'in',
          timestamp: serverTimestamp(),
        });
      }
      await loadData();
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;

  if (!user) {
    return (
      <div style={styles.page}>
        <div style={styles.loginCard}>
          <div style={styles.logo}>
            <img src={require('./assets/logo2.png')} alt="Reliable Handyman" style={{ height: 19 }} />
          </div>
          <div style={styles.loginSub}>Technician Portal</div>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input type="email" placeholder="Email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required style={styles.input} />
            <input type="password" placeholder="Password" value={loginPass} onChange={e => setLoginPass(e.target.value)} required style={styles.input} />
            {loginError && <div style={styles.error}>{loginError}</div>}
            <button type="submit" style={styles.btnPrimary}>Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div style={styles.page}>
        <div style={styles.loginCard}>
          <div style={{ ...styles.logo, color: '#C0392B' }}>Access Denied</div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', textAlign: 'center', lineHeight: 1.5 }}>
            Your account is not set up as an employee. Contact your manager.
          </p>
          <button onClick={() => signOut(auth)} style={{ ...styles.btnPrimary, marginTop: 20 }}>Sign out</button>
        </div>
      </div>
    );
  }

  // Filter jobs
  const today = new Date().toISOString().split('T')[0];
  const todayJobs = jobs.filter(j => j.scheduledDate === today && j.status !== 'Completed' && j.status !== 'Done');
  const upcomingJobs = jobs.filter(j => {
    if (j.status === 'Completed' || j.status === 'Done') return false;
    if (!j.scheduledDate) return true;
    return j.scheduledDate > today;
  }).sort((a, b) => (a.scheduledDate || '').localeCompare(b.scheduledDate || ''));
  const completedJobs = jobs.filter(j => j.status === 'Completed' || j.status === 'Done')
    .sort((a, b) => (b.completedAt?.seconds || 0) - (a.completedAt?.seconds || 0));

  if (selectedJob) {
    return <JobView job={selectedJob} employee={employee} onBack={() => setSelectedJob(null)} onUpdate={loadData} showToast={showToast} toast={toast} dismissToast={() => setToast(null)} />;
  }

  return (
    <div style={styles.appLayout}>
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      {/* Top bar */}
      <header style={styles.header}>
        <style>{`
          @media (max-width: 600px) {
            .tech-header-name-wrap { display: none !important; }
            .tech-header-divider { display: none !important; }
          }
        `}</style>
        <div style={styles.headerInner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <img src={require('./assets/logo2.png')} alt="Reliable Handyman" style={{ height: 19 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={styles.headerAvatar}>{getInitials(employee.name)}</div>
              <div className="tech-header-name-wrap" style={{ minWidth: 0 }}>
                <div style={styles.headerName}>{employee.name}</div>
                <div style={styles.headerRolePill}>
                  {employee.role === 'admin' ? 'Admin' : employee.role === 'lead' ? 'Lead Tech' : 'Technician'}
                </div>
              </div>
            </div>
            <div className="tech-header-divider" style={styles.headerDivider} />
            <button style={styles.signoutBtn} onClick={() => signOut(auth)}>Sign Out</button>
          </div>
        </div>
      </header>

      {/* Page content wrapper — 800px max */}
      <div style={styles.contentWrap}>

        {/* Quick-action grid — clock + 3 tiles */}
        <div style={styles.tileGrid}>

          {/* Clock tile */}
          <button
            onClick={handleClockInOut}
            style={{
              ...styles.actionTile,
              background: isClockedIn ? T.gradGreen : T.gradCard,
              color: isClockedIn ? '#fff' : T.text,
              border: isClockedIn ? 'none' : `1px solid ${T.hairline}`,
              boxShadow: isClockedIn
                ? '0 8px 24px rgba(29,107,69,0.18), inset 0 1px 0 rgba(255,255,255,0.12)'
                : T.shadowSm,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9,
                background: isClockedIn ? 'rgba(255,255,255,0.18)' : T.greenLight,
                color: isClockedIn ? '#fff' : T.greenDeep,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <span style={{
                fontSize: 9, fontWeight: 700,
                color: isClockedIn ? 'rgba(255,255,255,0.85)' : T.greenDeep,
                background: isClockedIn ? 'rgba(255,255,255,0.16)' : T.greenLight,
                padding: '3px 8px', borderRadius: 999,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                {isClockedIn ? 'On the clock' : 'Tap to start'}
              </span>
            </div>
            <div style={{
              fontSize: isClockedIn ? 22 : 16,
              fontWeight: isClockedIn ? 400 : 700,
              fontFamily: isClockedIn ? "'DM Serif Display', serif" : "'Montserrat', sans-serif",
              letterSpacing: isClockedIn ? '-0.02em' : '0.005em',
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'left',
            }}>
              {isClockedIn && lastPunch.timestamp?.seconds
                ? formatElapsed(nowTick - lastPunch.timestamp.seconds * 1000)
                : 'Clock in'}
            </div>
            <div style={{
              fontSize: 10, fontWeight: 600,
              color: isClockedIn ? 'rgba(255,255,255,0.7)' : T.textLight,
              marginTop: 4, letterSpacing: '0.04em', textTransform: 'uppercase',
              textAlign: 'left',
            }}>
              {new Date(nowTick).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
          </button>

          {/* Time Off Request */}
          <button
            onClick={() => showToast('Time off request — coming soon', 'info')}
            style={styles.actionTile}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 9,
              background: T.greenLight, color: T.greenDeep,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div style={styles.actionTileTitle}>Time Off</div>
            <div style={styles.actionTileSub}>Request days off</div>
          </button>

          {/* Truck Inventory */}
          <button
            onClick={() => showToast('Truck inventory — coming soon', 'info')}
            style={styles.actionTile}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 9,
              background: T.greenLight, color: T.greenDeep,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13"/>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                <circle cx="5.5" cy="18.5" r="2.5"/>
                <circle cx="18.5" cy="18.5" r="2.5"/>
              </svg>
            </div>
            <div style={styles.actionTileTitle}>Truck Inventory</div>
            <div style={styles.actionTileSub}>Stock & supplies</div>
          </button>

          {/* Repair Guides */}
          <button
            onClick={() => showToast('Repair guides — coming soon', 'info')}
            style={styles.actionTile}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 9,
              background: T.greenLight, color: T.greenDeep,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
            </div>
            <div style={styles.actionTileTitle}>Repair Guides</div>
            <div style={styles.actionTileSub}>How-to references</div>
          </button>
        </div>

      {/* Today's jobs */}
      <main style={styles.main}>
        <div style={styles.todayHeader}>
          <div style={styles.todayTitle}>Today's Jobs</div>
          <div style={styles.todayCount}>{todayJobs.length}</div>
        </div>
        {todayJobs.length === 0 ? (
          <div style={styles.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>No jobs today</div>
            <div style={{ fontSize: 13, color: '#9B9487', marginTop: 6 }}>Enjoy your day off, or check back later.</div>
          </div>
        ) : (
          todayJobs.map(job => <JobCard key={job.id} job={job} onClick={() => setSelectedJob(job)} />)
        )}
      </main>
      </div>
    </div>
  );
}

/* ── Format elapsed time ms → "Hh Mm Ss" ── */
function formatElapsed(ms) {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/* ── Get 2-letter initials from a name ── */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ── Toast (success / error / info) ── */
function Toast({ toast, onDismiss }) {
  if (!toast) return null;
  const isError = toast.type === 'error';
  const isSuccess = toast.type === 'success';
  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed',
        top: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: isError
          ? 'linear-gradient(180deg, #B73B3B 0%, #8C2828 100%)'
          : isSuccess
            ? 'linear-gradient(180deg, #1D7A4D 0%, #155838 100%)'
            : 'linear-gradient(180deg, #2A2825 0%, #1A1A18 100%)',
        color: '#fff',
        padding: '13px 20px',
        borderRadius: 999,
        fontFamily: "'Montserrat', sans-serif",
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: '0.005em',
        boxShadow: isError
          ? '0 8px 28px rgba(160,40,40,0.32), inset 0 1px 0 rgba(255,255,255,0.18)'
          : '0 8px 28px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        animation: 'tech-toast-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        cursor: 'pointer',
        maxWidth: 'min(90vw, 460px)',
      }}
    >
      <style>{`@keyframes tech-toast-in { from { opacity: 0; transform: translate(-50%, -20px); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>
      {isError ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      ) : isSuccess ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      )}
      <span style={{ lineHeight: 1.4 }}>{toast.message}</span>
    </div>
  );
}

/* ── Job Card ── */
function JobCard({ job, onClick }) {
  const statusColor = {
    'Approved': { bg: '#E8F1F8', color: '#1E6091' },
    'Scheduled': { bg: '#EAF7F0', color: '#1D6B45' },
    'In Progress': { bg: '#FEF3E7', color: '#D4880F' },
    'Completed': { bg: '#EAF7F0', color: '#1D6B45' },
    'Done': { bg: '#EAF7F0', color: '#1D6B45' },
  }[job.status] || { bg: '#F5F2ED', color: '#6B6559' };

  return (
    <div style={styles.jobCard} onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A18', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.title}
          </div>
          <div style={{ fontSize: 12, color: '#6B6559', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {(job.propertyId || job.accountNumber) && (
              <span style={{ background: '#EAF7F0', color: '#1D6B45', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 3, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', letterSpacing: 0.5 }}>
                {job.propertyId || job.accountNumber}
              </span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {job.clientName || job.contactName || '—'}
            </span>
          </div>
        </div>
        <span style={{ ...styles.statusBadge, background: statusColor.bg, color: statusColor.color }}>
          {job.status}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#9B9487', flexWrap: 'wrap' }}>
        {job.scheduledDate && (
          <span>📅 {new Date(job.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {job.scheduledTime && ` @ ${job.scheduledTime}`}
          </span>
        )}
        {job.contactAddress && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50%' }}>📍 {job.contactAddress}</span>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   JOB VIEW (detail screen)
   ══════════════════════════════════════════════ */
function JobView({ job, employee, onBack, onUpdate, showToast, toast, dismissToast }) {
  const [saving, setSaving] = useState(false);
  const [sendingOMW, setSendingOMW] = useState(false);

  // Site conditions (pre-repair)
  const [siteConditionNotes, setSiteConditionNotes] = useState(job.siteConditionNotes || '');
  const [siteConditionPhotos, setSiteConditionPhotos] = useState([]);
  const [uploadingSitePhotos, setUploadingSitePhotos] = useState(false);
  const [siteConditionsSaved, setSiteConditionsSaved] = useState(!!(job.siteConditionNotes || (job.siteConditionPhotos && job.siteConditionPhotos.length > 0)));

  // Completion form (post-repair)
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [hoursWorked, setHoursWorked] = useState('');
  const [materials, setMaterials] = useState([{ description: '' }]);

  const handleOnMyWay = async () => {
    setSendingOMW(true);
    try {
      const resp = await fetch('https://us-central1-reliable-handyman.cloudfunctions.net/sendOnMyWaySMS', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workOrderId: job.id, techName: employee.name }),
      });
      const data = await resp.json();
      if (data.success) {
        showToast('On-my-way text sent to client!', 'success');
      } else {
        showToast('Error: ' + (data.error || 'Could not send SMS'), 'error');
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
    setSendingOMW(false);
  };

  /* ── Start Job: requires site conditions first ── */
  const handleStartJob = async () => {
    setSaving(true);
    try {
      // Upload site condition photos if any
      let sitePhotoURLs = job.siteConditionPhotos || [];
      if (siteConditionPhotos.length > 0) {
        setUploadingSitePhotos(true);
        for (const file of siteConditionPhotos) {
          const fileName = `${Date.now()}_site_${file.name}`;
          const storageRef = ref(storage, `siteConditions/${job.propertyId || job.accountNumber || job.clientId || 'general'}/${fileName}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          sitePhotoURLs.push(url);
        }
        setUploadingSitePhotos(false);
      }

      await updateDoc(doc(db, 'workOrders', job.id), {
        status: 'In Progress',
        startedAt: serverTimestamp(),
        siteConditionNotes: siteConditionNotes || '',
        siteConditionPhotos: sitePhotoURLs,
        siteConditionDocumentedBy: employee.name,
        siteConditionDocumentedAt: serverTimestamp(),
      });
      await onUpdate();
      onBack();
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
    setSaving(false);
  };

  const handleSitePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSiteConditionPhotos([...siteConditionPhotos, ...files]);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setCompletionPhotos([...completionPhotos, ...files]);
  };

  const addMaterialRow = () => {
    setMaterials([...materials, { description: '' }]);
  };

  const updateMaterial = (i, field, value) => {
    const updated = [...materials];
    updated[i][field] = value;
    setMaterials(updated);
  };

  const removeMaterial = (i) => {
    setMaterials(materials.filter((_, idx) => idx !== i));
  };

  const handleCompleteJob = async () => {
    if (!hoursWorked || Number(hoursWorked) <= 0) {
      showToast('Please enter hours worked.', 'error');
      return;
    }
    setSaving(true);
    try {
      // Upload completion photos
      setUploadingPhotos(true);
      const photoURLs = [];
      for (const file of completionPhotos) {
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `jobPhotos/${job.propertyId || job.accountNumber || job.clientId || 'general'}/${fileName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        photoURLs.push(url);
      }
      setUploadingPhotos(false);

      // Log time entry
      await addDoc(collection(db, 'timeEntries'), {
        workOrderId: job.id,
        workOrderTitle: job.title,
        clientId: job.clientId,
        propertyId: job.propertyId || null,
        accountNumber: job.accountNumber || null,
        clientEmail: job.clientEmail,
        clientName: job.clientName || job.contactName || '',
        hoursWorked: Number(hoursWorked),
        techName: employee.name,
        notes: completionNotes,
        createdAt: serverTimestamp(),
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      });

      // Log material entries (description only — cost tied to account via Home Depot)
      const validMaterials = materials.filter(m => m.description);
      for (const mat of validMaterials) {
        await addDoc(collection(db, 'materialEntries'), {
          workOrderId: job.id,
          workOrderTitle: job.title,
          clientId: job.clientId,
          propertyId: job.propertyId || null,
          accountNumber: job.accountNumber || null,
          clientEmail: job.clientEmail,
          description: mat.description,
          techName: employee.name,
          createdAt: serverTimestamp(),
        });
      }

      // Update work order
      const existingPhotos = job.jobPhotos || [];
      await updateDoc(doc(db, 'workOrders', job.id), {
        status: 'Completed',
        completedAt: serverTimestamp(),
        completedBy: employee.name,
        jobPhotos: [...existingPhotos, ...photoURLs],
        completionNotes,
        hoursWorked: Number(hoursWorked),
        materialsUsed: validMaterials.map(m => m.description),
      });

      await onUpdate();
      showToast('Job marked complete!', 'success');
      onBack();
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div style={styles.jobViewLayout}>
      <Toast toast={toast} onDismiss={dismissToast} />
      <header style={styles.jobViewHeader}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.jobViewTitle}>{job.title}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {job.woNumber && <span style={{ fontSize: 11, color: '#9B9487' }}>{job.woNumber}</span>}
          {(job.propertyId || job.accountNumber) && (
            <span style={{ background: '#EAF7F0', color: '#1D6B45', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', letterSpacing: 0.5 }}>
              {job.propertyId || job.accountNumber}
            </span>
          )}
        </div>
      </header>

      <div style={styles.contentWrap}>
      <main style={styles.main}>
        {/* Property ID + Property Notes Banner */}
        {(job.propertyId || job.accountNumber) && (
          <div style={{ background: '#EAF7F0', border: '1px solid #B5D9C5', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#1D6B45', textTransform: 'uppercase', letterSpacing: 1 }}>Property</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1D6B45', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', letterSpacing: 0.8, marginTop: 2 }}>
                  {job.propertyId || job.accountNumber}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#4A7058', textAlign: 'right' }}>
                Use this ID at Home Depot<br/>and on any receipts
              </div>
            </div>
          </div>
        )}

        {/* Property Notes — access codes, dogs, shutoff location, etc. */}
        {job.propertyNotes && (
          <div style={{ background: '#FEF7E0', border: '1px solid #F5DFA0', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>⚠️</span>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A5A18', textTransform: 'uppercase', letterSpacing: 1 }}>
                Property Notes — read before arriving
              </div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: '#3A2E0A', whiteSpace: 'pre-wrap' }}>
              {job.propertyNotes}
            </div>
          </div>
        )}

        {/* Status + Quick Actions */}
        <div style={styles.jobViewSection}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9B9487', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Status</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1D6B45' }}>{job.status}</div>

          {(job.status === 'Scheduled' || job.status === 'Approved') && (
            <button style={{ ...styles.bigBtn, marginTop: 14 }} onClick={handleOnMyWay} disabled={sendingOMW}>
              {sendingOMW ? 'Sending...' : '🚗 On My Way — Send Text'}
            </button>
          )}
        </div>

        {/* Client Info */}
        <div style={styles.jobViewSection}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9B9487', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Client</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A18', marginBottom: 10 }}>
            {job.clientName || job.contactName || '—'}
          </div>
          {(job.contactPhone || job.clientPhone) && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <a href={`tel:${job.contactPhone || job.clientPhone}`} style={styles.contactBtn}>📞 Call</a>
              <a href={`sms:${job.contactPhone || job.clientPhone}`} style={styles.contactBtn}>💬 Text</a>
            </div>
          )}
          {job.contactAddress && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(job.contactAddress)}`} target="_blank" rel="noopener noreferrer" style={styles.addressLink}>
              📍 {job.contactAddress}
            </a>
          )}

          {/* Secondary contacts (spouse, tenant, property manager) */}
          {(job.secondaryContacts || []).length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #E8E4DC' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9B9487', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Other contacts at this property
              </div>
              {(job.secondaryContacts || []).map((sc, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #F0EDE6' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A18' }}>{sc.name || '—'}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B6559', textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 1 }}>
                      {(sc.role || 'other').replace('-', ' ')}
                    </div>
                  </div>
                  {sc.phone && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <a href={`tel:${sc.phone}`} style={{ ...styles.contactBtn, padding: '6px 10px', fontSize: 12 }}>📞</a>
                      <a href={`sms:${sc.phone}`} style={{ ...styles.contactBtn, padding: '6px 10px', fontSize: 12 }}>💬</a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Job Details */}
        {job.description && (
          <div style={styles.jobViewSection}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9B9487', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Description</div>
            <div style={{ fontSize: 14, color: '#1A1A18', lineHeight: 1.6 }}>{job.description}</div>
          </div>
        )}

        {/* Material list from admin */}
        {job.materialList && (
          <div style={{ ...styles.jobViewSection, background: '#FEF9E7', border: '1px solid #F5DFA0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D4880F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>🛒 Materials to Bring</div>
            <div style={{ fontSize: 14, color: '#1A1A18', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{job.materialList}</div>
          </div>
        )}

        {/* Schedule */}
        <div style={styles.jobViewSection}>
          <div style={styles.metaGrid}>
            {job.scheduledDate && (
              <div>
                <div style={styles.metaLabel}>Scheduled</div>
                <div style={styles.metaValue}>
                  {new Date(job.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {job.scheduledTime && ` @ ${job.scheduledTime}`}
                </div>
              </div>
            )}
            {job.priority && job.priority !== 'Normal' && (
              <div>
                <div style={styles.metaLabel}>Priority</div>
                <div style={{ ...styles.metaValue, color: '#D4880F' }}>{job.priority}</div>
              </div>
            )}
          </div>
        </div>

        {/* Client-uploaded photos */}
        {job.photos && job.photos.length > 0 && (
          <div style={styles.jobViewSection}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9B9487', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Photos from client</div>
            <div style={styles.photoGrid}>
              {job.photos.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={styles.photoThumb}>
                  <img src={url} alt={`Client ${i + 1}`} style={styles.photoImg} />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
           SITE CONDITIONS (pre-repair documentation)
           ═══════════════════════════════════════ */}
        {(job.status === 'Scheduled' || job.status === 'Approved') && (
          <div style={{ ...styles.jobViewSection, background: '#EDF2FE', border: '1px solid #C5D9F7' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A56DB', marginBottom: 4 }}>📋 Document Site Conditions</div>
            <div style={{ fontSize: 12, color: '#4A6FA5', marginBottom: 14, lineHeight: 1.5 }}>
              Take photos and note any existing damage or conditions before starting work. This protects you and the client.
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Site Photos (before work)</label>
              <input type="file" multiple accept="image/*" capture="environment" onChange={handleSitePhotoSelect} style={styles.formInput} />
              {siteConditionPhotos.length > 0 && (
                <div style={{ fontSize: 12, color: '#1A56DB', marginTop: 4, fontWeight: 600 }}>
                  {siteConditionPhotos.length} photo{siteConditionPhotos.length !== 1 ? 's' : ''} selected
                </div>
              )}
              {/* Show previously saved site photos */}
              {job.siteConditionPhotos && job.siteConditionPhotos.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: '#4A6FA5', marginBottom: 6 }}>Previously saved:</div>
                  <div style={styles.photoGrid}>
                    {job.siteConditionPhotos.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={styles.photoThumb}>
                        <img src={url} alt={`Site ${i + 1}`} style={styles.photoImg} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Existing conditions / notes</label>
              <textarea
                value={siteConditionNotes}
                onChange={e => setSiteConditionNotes(e.target.value)}
                placeholder="Note any existing damage, access issues, pets, special instructions..."
                rows={3}
                style={{ ...styles.formInput, resize: 'vertical' }}
              />
            </div>

            <button
              style={{ ...styles.bigBtn, marginTop: 8 }}
              onClick={handleStartJob}
              disabled={saving || uploadingSitePhotos}
            >
              {uploadingSitePhotos ? 'Uploading photos...' : saving ? 'Starting...' : '▶ Document & Start Job'}
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════
           PREVIOUSLY DOCUMENTED SITE CONDITIONS (shown when In Progress)
           ═══════════════════════════════════════ */}
        {job.status === 'In Progress' && (job.siteConditionNotes || (job.siteConditionPhotos && job.siteConditionPhotos.length > 0)) && (
          <div style={{ ...styles.jobViewSection, background: '#F0F4FF', border: '1px solid #D4DFF7' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4A6FA5', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>📋 Site Conditions (documented)</div>
            {job.siteConditionNotes && (
              <div style={{ fontSize: 13, color: '#1A1A18', lineHeight: 1.5, marginBottom: 10 }}>{job.siteConditionNotes}</div>
            )}
            {job.siteConditionPhotos && job.siteConditionPhotos.length > 0 && (
              <div style={styles.photoGrid}>
                {job.siteConditionPhotos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={styles.photoThumb}>
                    <img src={url} alt={`Site ${i + 1}`} style={styles.photoImg} />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════
           COMPLETE JOB FORM (post-repair)
           ═══════════════════════════════════════ */}
        {job.status === 'In Progress' && (
          <div style={{ ...styles.jobViewSection, background: '#FEF3CD', border: '1px solid #F5DFA0' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#D4880F', marginBottom: 14 }}>Complete Job</div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Hours Worked *</label>
              <input type="number" step="0.25" value={hoursWorked} onChange={e => setHoursWorked(e.target.value)} placeholder="2.5" style={styles.formInput} />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>After Photos (optional)</label>
              <input type="file" multiple accept="image/*" capture="environment" onChange={handlePhotoSelect} style={styles.formInput} />
              {completionPhotos.length > 0 && (
                <div style={{ fontSize: 12, color: '#6B6559', marginTop: 4 }}>
                  {completionPhotos.length} photo{completionPhotos.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Materials Purchased {(job.propertyId || job.accountNumber) && <span style={{ fontWeight: 400, color: '#1D6B45' }}>— use {job.propertyId || job.accountNumber} at Home Depot</span>}</label>
              {materials.map((mat, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    placeholder="Item description (e.g. 3/4in copper elbow x2)"
                    value={mat.description}
                    onChange={e => updateMaterial(i, 'description', e.target.value)}
                    style={{ ...styles.formInput, flex: 1 }}
                  />
                  {materials.length > 1 && (
                    <button onClick={() => removeMaterial(i)} style={styles.removeBtn}>×</button>
                  )}
                </div>
              ))}
              <button onClick={addMaterialRow} style={styles.addMaterialBtn}>+ Add Material</button>
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Notes (optional)</label>
              <textarea
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                placeholder="Any notes about the work done, recommendations for client..."
                rows={3}
                style={{ ...styles.formInput, resize: 'vertical' }}
              />
            </div>

            <button
              style={{ ...styles.bigBtn, marginTop: 8 }}
              onClick={handleCompleteJob}
              disabled={saving || uploadingPhotos}
            >
              {uploadingPhotos ? 'Uploading photos...' : saving ? 'Saving...' : '✓ Mark Job Complete'}
            </button>
          </div>
        )}

        {/* Already completed */}
        {(job.status === 'Completed' || job.status === 'Done') && (
          <div style={{ ...styles.jobViewSection, background: '#EAF7F0', border: '1px solid #C8E6D5', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 6 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1D6B45' }}>Job Complete</div>
            {job.completedAt?.seconds && (
              <div style={{ fontSize: 12, color: '#6B6559', marginTop: 4 }}>
                {new Date(job.completedAt.seconds * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
            {job.hoursWorked && (
              <div style={{ fontSize: 13, color: '#1A1A18', marginTop: 8 }}>{job.hoursWorked} hours worked</div>
            )}
            {job.completionNotes && (
              <div style={{ fontSize: 13, color: '#1A1A18', marginTop: 12, textAlign: 'left', background: '#fff', padding: 10, borderRadius: 8 }}>
                {job.completionNotes}
              </div>
            )}
            {job.materialsUsed && job.materialsUsed.length > 0 && (
              <div style={{ marginTop: 12, textAlign: 'left' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B6559', textTransform: 'uppercase', marginBottom: 6 }}>Materials</div>
                {job.materialsUsed.map((m, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#1A1A18', padding: '3px 0' }}>• {m}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   STYLES
   ══════════════════════════════════════════════ */
/* ── Design tokens (Apple polish) ──
   Stack two shadows for soft depth. Pill radius for CTAs. Hairline borders.
   Tabular nums on time/numbers via fontVariantNumeric. */
const T = {
  green: '#1D6B45',
  greenDeep: '#155838',
  greenLight: '#EAF7F0',
  greenSoft: '#3BA672',
  bg: '#FAFAF7',
  surface: '#fff',
  text: '#1A1A18',
  textMid: '#6B6559',
  textLight: '#9B9487',
  textFaint: '#C5BFB2',
  hairline: 'rgba(26,26,24,0.08)',
  hairlineStrong: 'rgba(26,26,24,0.14)',
  shadowSm: '0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)',
  shadowLg: '0 2px 4px rgba(0,0,0,0.04), 0 18px 40px rgba(0,0,0,0.06)',
  gradGreen: 'linear-gradient(180deg, #1D7A4D 0%, #155838 100%)',
  gradCard: 'linear-gradient(180deg, #ffffff 0%, #fbfaf7 100%)',
  gradCream: 'linear-gradient(180deg, #FAFAF7 0%, #F5F2ED 100%)',
  ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

const styles = {
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: "'Montserrat', sans-serif",
    color: T.textLight,
    background: T.bg,
  },

  /* ── LOGIN ── */
  page: {
    minHeight: '100vh',
    background: T.gradCream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Montserrat', sans-serif",
    padding: 20,
  },
  loginCard: {
    background: T.gradCard,
    padding: '40px 36px 32px',
    borderRadius: 18,
    maxWidth: 380,
    width: '100%',
    border: `1px solid ${T.hairline}`,
    boxShadow: T.shadowLg,
  },
  logo: {
    fontFamily: "'DM Serif Display', serif",
    fontSize: 26,
    letterSpacing: '-0.018em',
    textAlign: 'center',
    marginBottom: 6,
    color: T.text,
  },
  loginSub: {
    fontSize: 11,
    color: T.textMid,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.16em',
    fontWeight: 600,
    marginBottom: 32,
  },
  loginForm: { display: 'flex', flexDirection: 'column', gap: 14 },
  input: {
    padding: '13px 16px',
    border: `1px solid ${T.hairlineStrong}`,
    borderRadius: 12,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 15,
    background: T.surface,
    color: T.text,
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
    transition: `border-color 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },
  error: {
    background: '#FDF2F2',
    color: '#C0392B',
    padding: '10px 14px',
    borderRadius: 10,
    fontSize: 12,
    textAlign: 'center',
    border: '1px solid #F5C6C6',
  },
  btnPrimary: {
    background: T.gradGreen,
    color: '#fff',
    border: 'none',
    padding: '14px',
    borderRadius: 999,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.01em',
    cursor: 'pointer',
    marginTop: 10,
    boxShadow: `${T.shadowSm}, inset 0 1px 0 rgba(255,255,255,0.12)`,
    transition: `transform 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },

  /* ── APP LAYOUT ── */
  appLayout: {
    minHeight: '100vh',
    background: T.bg,
    fontFamily: "'Montserrat', sans-serif",
    paddingBottom: 60,
  },
  contentWrap: {
    maxWidth: 800,
    margin: '0 auto',
    width: '100%',
  },
  header: {
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'saturate(180%) blur(20px)',
    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
    padding: 0,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    borderBottom: `1px solid ${T.hairline}`,
  },
  headerInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '14px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 14,
  },
  headerDivider: {
    width: 1,
    height: 26,
    background: T.hairlineStrong,
  },
  headerAvatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: T.gradGreen,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 12,
    fontFamily: "'Montserrat', sans-serif",
    flexShrink: 0,
    letterSpacing: '0.02em',
    boxShadow: `${T.shadowSm}, inset 0 1px 0 rgba(255,255,255,0.18)`,
  },
  headerName: {
    fontFamily: "'DM Serif Display', serif",
    fontSize: 16,
    color: T.text,
    lineHeight: 1.1,
    letterSpacing: '-0.01em',
  },
  headerRolePill: {
    display: 'inline-block',
    marginTop: 5,
    padding: '3px 9px',
    background: T.greenLight,
    color: T.greenDeep,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderRadius: 999,
  },
  signoutBtn: {
    background: T.surface,
    border: `1px solid ${T.hairlineStrong}`,
    borderRadius: 999,
    color: T.textMid,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 500,
    padding: '7px 14px',
    cursor: 'pointer',
    transition: `transform 0.2s ${T.ease}, border-color 0.2s ${T.ease}, color 0.2s ${T.ease}`,
  },

  /* ── QUICK-ACTION TILE GRID ── */
  tileGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10,
    margin: '14px 16px 4px',
  },
  actionTile: {
    background: T.gradCard,
    border: `1px solid ${T.hairline}`,
    borderRadius: 14,
    padding: '14px 16px',
    boxShadow: T.shadowSm,
    fontFamily: "'Montserrat', sans-serif",
    cursor: 'pointer',
    textAlign: 'left',
    transition: `transform 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
    minHeight: 92,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  actionTileTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: T.text,
    letterSpacing: '-0.005em',
    marginBottom: 3,
  },
  actionTileSub: {
    fontSize: 11,
    color: T.textLight,
    fontWeight: 500,
    letterSpacing: '0.005em',
  },

  /* ── CLOCK BANNER (legacy, kept for refs) ── */
  clockBanner: {
    margin: '12px 16px',
    padding: '14px 18px',
    borderRadius: 14,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: T.gradCard,
    border: `1px solid ${T.hairline}`,
    boxShadow: T.shadowSm,
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
  },
  clockBtn: {
    padding: '9px 16px',
    border: 'none',
    borderRadius: 999,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.005em',
    cursor: 'pointer',
    transition: `transform 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },

  /* ── TODAY HEADER ── */
  todayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 4px 14px',
    marginBottom: 4,
  },
  todayTitle: {
    fontFamily: "'DM Serif Display', serif",
    fontSize: 24,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
    color: T.text,
  },
  todayCount: {
    background: T.gradGreen,
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 999,
    minWidth: 28,
    textAlign: 'center',
    letterSpacing: '0.02em',
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.15)`,
    fontVariantNumeric: 'tabular-nums',
  },

  /* ── TABS (kept for compatibility — currently single-view) ── */
  tabNav: {
    display: 'flex',
    background: T.surface,
    borderTop: `1px solid ${T.hairline}`,
    borderBottom: `1px solid ${T.hairline}`,
  },
  tab: {
    flex: 1,
    padding: '14px 10px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: T.textLight,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    transition: `color 0.2s ${T.ease}, border-color 0.2s ${T.ease}`,
  },
  tabActive: {
    color: T.greenDeep,
    borderBottomColor: T.greenDeep,
  },
  tabBadge: {
    background: T.greenLight,
    color: T.greenDeep,
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 999,
    fontVariantNumeric: 'tabular-nums',
  },

  main: { padding: 16 },

  empty: {
    textAlign: 'center',
    padding: '64px 24px',
    color: T.textMid,
    background: T.gradCard,
    border: `1px solid ${T.hairline}`,
    borderRadius: 18,
    boxShadow: T.shadowSm,
  },

  /* ── JOB CARD ── */
  jobCard: {
    background: T.gradCard,
    border: `1px solid ${T.hairline}`,
    borderRadius: 14,
    padding: '16px 18px',
    marginBottom: 10,
    cursor: 'pointer',
    boxShadow: T.shadowSm,
    transition: `transform 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },

  statusBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    flexShrink: 0,
  },

  /* ── JOB VIEW (detail) ── */
  jobViewLayout: {
    minHeight: '100vh',
    background: T.bg,
    fontFamily: "'Montserrat', sans-serif",
    paddingBottom: 40,
  },
  jobViewHeader: {
    background: T.text,
    padding: '18px 20px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.16)',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: T.greenSoft,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 10,
    letterSpacing: '0.01em',
  },
  jobViewTitle: {
    fontFamily: "'DM Serif Display', serif",
    color: '#fff',
    fontSize: 22,
    fontWeight: 400,
    letterSpacing: '-0.018em',
    lineHeight: 1.15,
  },
  jobViewSub: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 11,
    marginTop: 4,
    letterSpacing: '0.02em',
  },
  jobViewSection: {
    background: T.gradCard,
    border: `1px solid ${T.hairline}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 12,
    boxShadow: T.shadowSm,
  },

  /* ── BIG CTAs ── */
  bigBtn: {
    width: '100%',
    padding: '14px',
    background: T.gradGreen,
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.01em',
    cursor: 'pointer',
    boxShadow: `${T.shadowSm}, inset 0 1px 0 rgba(255,255,255,0.12)`,
    transition: `transform 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },
  bigBtnOutline: {
    width: '100%',
    padding: '14px',
    background: T.surface,
    color: T.text,
    border: `1px solid ${T.hairlineStrong}`,
    borderRadius: 999,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.01em',
    cursor: 'pointer',
    boxShadow: T.shadowSm,
    transition: `transform 0.2s ${T.ease}, border-color 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },

  contactBtn: {
    flex: 1,
    padding: '11px 14px',
    background: T.greenLight,
    color: T.greenDeep,
    border: `1px solid #C8E6D5`,
    borderRadius: 999,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    textDecoration: 'none',
    transition: `transform 0.2s ${T.ease}`,
  },
  addressLink: {
    display: 'block',
    padding: '12px 14px',
    background: T.bg,
    color: T.greenDeep,
    border: `1px solid ${T.hairline}`,
    borderRadius: 12,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    transition: `border-color 0.2s ${T.ease}`,
  },

  /* ── META GRID ── */
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: T.textMid,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 5,
  },
  metaValue: {
    fontSize: 13,
    color: T.text,
    fontWeight: 600,
    letterSpacing: '-0.005em',
  },

  /* ── PHOTO GRID ── */
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  photoThumb: {
    aspectRatio: '1',
    borderRadius: 12,
    overflow: 'hidden',
    border: `1px solid ${T.hairline}`,
    display: 'block',
    boxShadow: T.shadowSm,
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },

  /* ── FORM ── */
  formField: { marginBottom: 16 },
  formLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: T.textMid,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 7,
  },
  formInput: {
    width: '100%',
    padding: '12px 14px',
    border: `1px solid ${T.hairlineStrong}`,
    borderRadius: 12,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 15,
    color: T.text,
    outline: 'none',
    background: T.surface,
    boxSizing: 'border-box',
    transition: `border-color 0.2s ${T.ease}, box-shadow 0.2s ${T.ease}`,
  },
  removeBtn: {
    width: 38,
    border: `1px solid ${T.hairlineStrong}`,
    background: T.surface,
    borderRadius: 999,
    fontSize: 18,
    color: '#C0392B',
    cursor: 'pointer',
    flexShrink: 0,
    transition: `background 0.2s ${T.ease}`,
  },
  addMaterialBtn: {
    padding: '10px 14px',
    background: 'none',
    border: `1px dashed ${T.hairlineStrong}`,
    borderRadius: 12,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: T.textMid,
    cursor: 'pointer',
    width: '100%',
    transition: `border-color 0.2s ${T.ease}, color 0.2s ${T.ease}, background 0.2s ${T.ease}`,
  },
};
